import { includes } from "lodash";
// eslint-disable-next-line no-restricted-imports
import { useRouter } from "next/router";
import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import BillingInformation from "rivals/components/pages/User/BillingInformation/container";
import RivalsButton from "rivals/components/shared/Button";
import InputWrapper from "rivals/components/shared/Form/Input";
import PromotionInput from "rivals/components/shared/Form/PromotionInput";
import Link from "rivals/components/shared/Link";
import SiteSelect from "rivals/components/shared/Subscription/SiteSelect";
import SubscriptionAgreement from "rivals/components/shared/Subscription/SubscriptionAgreement";
import { SubscriptionPlanSelection } from "rivals/components/shared/Subscription/SubscriptionPlan";
import SubscriptionPricing from "rivals/components/shared/Subscription/SubscriptionPricing";
import SubscriptionTerms from "rivals/components/shared/Subscription/SubscriptionTerms";
import { AlertTypes } from "rivals/redux/alert/reducer";
import { selectHasSelectedSiteAddOns } from "rivals/redux/rails/selectors";
import { State as AppState } from "rivals/redux/reducer";
import { selectSelectedAddOn } from "rivals/redux/registration/selectors";
import { selectSubscribableAddOns } from "rivals/redux/subscriptionAddons/selectors";
import {
  getSpyAddOnSites,
  isPayPalSubscription
} from "rivals/redux/user/selectors";
import { CUSTOM_EVENTS } from "rivals/shared/analytics/customEvents";
import { PATHS, PLANS, PurchasablePlan } from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { Site } from "rivals/shared/interfaces/Site";
import { setData } from "rivals/shared/utils/qa";
import styles from "./New.module.scss";
import strings from "./strings";
import { DispatchProps, StateProps } from "./types";

export type Props = StateProps & DispatchProps;

const SubscriptionNew: FunctionComponent<Props> = ({
  address,
  createSubscription,
  promotion,
  railsConstants,
  selectedSite,
  sendGAEvent,
  showAlert,
  submitting,
  subscriptions
}) => {
  const philterPage = "subscriptions-new";
  const [userSelectedSite, setSelectedSite] = useState<Site | undefined>(
    selectedSite
  );

  const {
    alreadySubscribed: alreadySubscribedString,
    alreadySubscribedSubtext,
    annualBullets,
    annualCostDescription,
    cancel,
    existingSpyCancel,
    existingSpyInfo,
    monthlyBullets,
    monthlyCostDescription,
    reviewYourSubscriptions,
    selectSitePrompt,
    selectSubscription,
    selectTeam,
    spyAddOnWillBeCancelled,
    subscribe
  } = strings;

  const router = useRouter();
  const hasSiteAddOn = useSelector(selectHasSelectedSiteAddOns);
  const currentSpySites = useSelector(getSpyAddOnSites);
  const isPayPal = useSelector(isPayPalSubscription);

  const {
    query: {
      registrationError,
      promo_code: urlPromoCode,
      ref: referAFriendCode
    }
  } = router;

  useEffect(() => {
    // registrationError may be present if subscription creation fails during registration.
    if (registrationError) {
      showAlert({
        message: registrationError as string,
        persisted: false,
        type: AlertTypes.SEVERE
      });
    }
  }, [registrationError, showAlert]);

  useEffect(() => {
    if (hasSiteAddOn) {
      showAlert({
        message: spyAddOnWillBeCancelled,
        persisted: false,
        type: AlertTypes.INFO
      });
    }
  }, [hasSiteAddOn, showAlert, spyAddOnWillBeCancelled]);

  const [promoCodeValue, setPromoCode] = useState(urlPromoCode as string);
  const [selectedPlan, selectPlan] = useState<PurchasablePlan>(PLANS.ANNUAL);
  const [isAutoRenewAgreed, setIsAutoRenewAgreed] = useState(false);
  const handleSelectedSite = (value: Site): void => {
    setSelectedSite(value);
  };

  const handleSelectedSiteCleared = (): void => {
    setSelectedSite(undefined);
  };

  const selectedAddOn = useSelector<AppState, AddOn | undefined>(state =>
    selectSelectedAddOn(state, selectedPlan)
  );

  const subscribableAddOns = useSelector<AppState, AddOn[]>(state =>
    selectSubscribableAddOns(state, selectedPlan)
  );

  if (!address || !railsConstants || !selectedSite) {
    return null;
  }

  const {
    annualCostDollars,
    autoRenewalOptInStates,
    monthlyCostDollars,
    monthlySavings,
    tax
  } = railsConstants;

  const alreadySubscribed =
    subscriptions &&
    userSelectedSite &&
    subscriptions.map(sub => sub.id).includes(userSelectedSite.id);

  const requiresAutoRenewalOptIn = address
    ? includes(autoRenewalOptInStates, address.state)
    : false;

  const buttonDisabled =
    submitting ||
    (requiresAutoRenewalOptIn && !isAutoRenewAgreed) ||
    !userSelectedSite?.subscribable;

  const alreadySubscribedElement = (
    <>
      <h1>{alreadySubscribedString}</h1>
      <div>
        <span>{alreadySubscribedSubtext}</span>
        <Link href={PATHS.SUBSCRIPTIONS}>{reviewYourSubscriptions}</Link>
      </div>
    </>
  );

  const spyOnSite =
    currentSpySites &&
    currentSpySites.find(addOn => userSelectedSite?.id == addOn.id);
  const existingSpyCancelString = spyOnSite ? (
    <p>
      {existingSpyInfo} {spyOnSite.name}. {existingSpyCancel}
    </p>
  ) : (
    ""
  );

  const selectSiteDropdown = (
    <InputWrapper className={styles.selectSite} label="Select a site">
      <SiteSelect
        onChange={handleSelectedSite}
        onClear={handleSelectedSiteCleared}
        placeholder={selectTeam}
      />
    </InputWrapper>
  );

  let completeSubscription;

  if (userSelectedSite?.subscribable) {
    completeSubscription = (
      <>
        <SubscriptionPricing
          addOn={selectedAddOn}
          address={address}
          isPayPal={isPayPal}
          isValid={!buttonDisabled}
          planType={selectedPlan}
          promoCodeValue={promoCodeValue}
          selectedSite={userSelectedSite}
          tax={tax}
        />
        {requiresAutoRenewalOptIn && (
          <SubscriptionAgreement
            isAutoRenewAgreed={isAutoRenewAgreed}
            onAutoRenewChange={(): void => {
              setIsAutoRenewAgreed(!isAutoRenewAgreed);
            }}
          />
        )}
        <div
          className={styles.cancelExistingSpy}
          {...setData(philterPage, "existing-spy-cancel")}
        >
          {existingSpyCancelString}
        </div>
        <SubscriptionTerms />
        <div className={styles.subscriptionButtons}>
          <RivalsButton
            backgroundColor={styles.transparent}
            borderColor={styles.transparent}
            buttonText={cancel}
            className={styles.cancelLink}
            data="subscriptions-cancel"
            inverted={true}
            key="cancelButton"
            url={PATHS.SUBSCRIPTIONS}
          />
          <RivalsButton
            buttonText={subscribe}
            className={styles.subscribeButton}
            data="subscriptions-new-submit"
            disabled={buttonDisabled}
            inverted
            onClick={(): void => {
              sendGAEvent(
                CUSTOM_EVENTS.createSubscription(
                  "subscription",
                  selectedPlan,
                  selectedSite.name
                )
              );
              createSubscription({
                addOnName: selectedAddOn?.name,
                autoRenewalOptIn: isAutoRenewAgreed,
                promoCode: promotion ? promoCodeValue : "",
                siteId: userSelectedSite.id,
                subscriptionPlan: selectedPlan
              });
            }}
          />
        </div>
      </>
    );
  } else {
    completeSubscription = (
      <div className={styles.sitePrompt}>{selectSitePrompt}</div>
    );
  }

  const subscribeElement = (
    <>
      <h1 className={styles.sectionHeader}>{selectSubscription}</h1>
      {!selectedSite.subscribable && selectSiteDropdown}
      <SubscriptionPlanSelection
        onChange={(e?: ChangeEvent<HTMLInputElement>): void => {
          e && selectPlan(e.target.value as PurchasablePlan);
        }}
        plans={[
          {
            bullets: annualBullets(userSelectedSite?.name ?? ""),
            cost: annualCostDescription(annualCostDollars),
            planType: PLANS.ANNUAL,
            savings: monthlySavings
          },
          {
            bullets: monthlyBullets(userSelectedSite?.name ?? ""),
            cost: monthlyCostDescription(monthlyCostDollars),
            planType: PLANS.MONTHLY
          }
        ]}
        promotion={promotion}
        selectedPlan={selectedPlan}
        subscribableAddOns={subscribableAddOns}
      />
      <hr className={styles.divider} />
      <BillingInformation className={styles.billingInformation} />
      <PromotionInput
        planType={selectedPlan}
        promoCodeValue={promoCodeValue}
        referAFriendCode={referAFriendCode as string}
        selectedSite={userSelectedSite}
        setPromoCode={setPromoCode}
      />
      {completeSubscription}
    </>
  );

  const element = alreadySubscribed
    ? alreadySubscribedElement
    : subscribeElement;

  return <div className={styles.subscriptionNew}>{element}</div>;
};

export default SubscriptionNew;
